import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  GraduationCap,
  Trophy,
  Users,
  Calendar,
  Clock,
  Star,
  ChevronRight,
  Play,
  CheckCircle,
  Award,
  Target,
  Zap
} from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';

// Training Programs Data
const PROGRAMS = [
  {
    id: '1',
    title: 'Sports Management Certification',
    category: 'Management',
    level: 'Professional',
    duration: '12 weeks',
    price: 'KES 75,000',
    rating: 4.9,
    students: 1250,
    image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=600&h=400&fit=crop',
    description: 'Comprehensive program covering sports organization management, event planning, and business operations.',
    modules: ['Sports Business Fundamentals', 'Event Management', 'Marketing & Sponsorship', 'Financial Management'],
    instructor: 'Dr. James Ochieng',
    featured: true
  },
  {
    id: '2',
    title: 'Football Coaching License - Level C',
    category: 'Coaching',
    level: 'Beginner',
    duration: '4 weeks',
    price: 'KES 25,000',
    rating: 4.8,
    students: 3400,
    image: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=600&h=400&fit=crop',
    description: 'Entry-level coaching certification recognized by Kenya Football Federation.',
    modules: ['Coaching Fundamentals', 'Training Sessions', 'Youth Development', 'Match Analysis'],
    instructor: 'Coach Peter Wanyama',
    featured: true
  },
  {
    id: '3',
    title: 'Sports Analytics & Data Science',
    category: 'Technical',
    level: 'Intermediate',
    duration: '8 weeks',
    price: 'KES 45,000',
    rating: 4.7,
    students: 890,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
    description: 'Learn to analyze sports performance data using Python, R, and visualization tools.',
    modules: ['Data Collection', 'Statistical Analysis', 'Performance Metrics', 'Visualization'],
    instructor: 'Sarah Njeri, PhD',
    featured: false
  },
  {
    id: '4',
    title: 'Sports Physiotherapy Fundamentals',
    category: 'Medical',
    level: 'Professional',
    duration: '10 weeks',
    price: 'KES 60,000',
    rating: 4.9,
    students: 650,
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=400&fit=crop',
    description: 'Essential physiotherapy techniques for sports professionals and athletes.',
    modules: ['Injury Prevention', 'Rehabilitation', 'Sports Massage', 'Recovery Protocols'],
    instructor: 'Dr. Grace Muthoni',
    featured: false
  },
  {
    id: '5',
    title: 'Sports Marketing Masterclass',
    category: 'Marketing',
    level: 'Intermediate',
    duration: '6 weeks',
    price: 'KES 35,000',
    rating: 4.6,
    students: 1100,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
    description: 'Master digital marketing strategies specific to the sports industry.',
    modules: ['Brand Building', 'Social Media', 'Sponsorship Sales', 'Fan Engagement'],
    instructor: 'Michael Otieno',
    featured: false
  },
  {
    id: '6',
    title: 'Referee & Officiating Course',
    category: 'Officiating',
    level: 'Beginner',
    duration: '3 weeks',
    price: 'KES 15,000',
    rating: 4.5,
    students: 2200,
    image: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=600&h=400&fit=crop',
    description: 'Official referee certification course for football and other sports.',
    modules: ['Rules & Regulations', 'Match Management', 'Fitness Requirements', 'Decision Making'],
    instructor: 'FIFA Certified Instructors',
    featured: false
  }
];

const CATEGORIES = ['All', 'Coaching', 'Management', 'Technical', 'Medical', 'Marketing', 'Officiating'];

const TrainingPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProgram, setSelectedProgram] = useState(null);

  const filteredPrograms = PROGRAMS.filter(program => 
    selectedCategory === 'All' || program.category === selectedCategory
  );

  const featuredPrograms = filteredPrograms.filter(p => p.featured);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-secondary/20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block bg-primary/20 text-primary px-4 py-1.5 rounded-full text-sm font-body mb-6">
              Level Up Your Career
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Sports Training & Certification Programs
            </h1>
            <p className="text-white/70 text-lg font-body mb-8">
              Access world-class training programs designed for Africa's sports industry. 
              From coaching licenses to sports management certifications.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 text-white/60">
                <GraduationCap size={20} className="text-primary" />
                <span className="font-body">20+ Programs</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Users size={20} className="text-primary" />
                <span className="font-body">10,000+ Students</span>
              </div>
              <div className="flex items-center gap-2 text-white/60">
                <Award size={20} className="text-primary" />
                <span className="font-body">Certified</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-6 border-b border-border sticky top-16 bg-background/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full font-body text-sm transition-all ${
                  selectedCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-card-bg border border-border text-white/60 hover:text-white hover:border-primary/50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      {featuredPrograms.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-8 flex items-center gap-2">
              <Star className="text-secondary" size={24} />
              Featured Programs
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featuredPrograms.map((program) => (
                <FeaturedProgramCard key={program.id} program={program} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Programs */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-white mb-8">
            All Training Programs ({filteredPrograms.length})
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPrograms.filter(p => !p.featured).map((program) => (
              <ProgramCard key={program.id} program={program} />
            ))}
          </div>

          {filteredPrograms.length === 0 && (
            <div className="text-center py-12 bg-card-bg rounded-xl border border-border">
              <GraduationCap className="w-12 h-12 text-white/30 mx-auto mb-4" />
              <p className="text-white/60 font-body">No programs found in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-card-bg/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Why Train With Michezonet?
            </h2>
            <p className="text-white/60 font-body max-w-2xl mx-auto">
              Our programs are designed by industry experts and recognized across Africa
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: 'Certified Programs', desc: 'Recognized by sports federations' },
              { icon: Users, title: 'Expert Instructors', desc: 'Learn from industry leaders' },
              { icon: Target, title: 'Practical Focus', desc: 'Hands-on learning experience' },
              { icon: Zap, title: 'Flexible Learning', desc: 'Online & in-person options' }
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/60 font-body text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Advance Your Sports Career?
          </h2>
          <p className="text-white/60 font-body mb-8">
            Join thousands of sports professionals who have upgraded their skills with Michezonet.
          </p>
          <Link to="/signup" className="btn-primary inline-flex items-center gap-2">
            Get Started Today <ChevronRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Featured Program Card
const FeaturedProgramCard = ({ program }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-card-bg border border-secondary/30 rounded-2xl overflow-hidden group"
  >
    <div className="relative h-52 overflow-hidden">
      <img 
        src={program.image} 
        alt={program.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
      <div className="absolute top-4 left-4 flex gap-2">
        <span className="bg-secondary text-white text-xs font-bold px-3 py-1 rounded-full">
          FEATURED
        </span>
        <span className="bg-black/50 text-white text-xs px-3 py-1 rounded-full">
          {program.level}
        </span>
      </div>
      <div className="absolute bottom-4 left-4 right-4">
        <span className="bg-primary/90 text-white text-xs px-3 py-1 rounded">
          {program.category}
        </span>
      </div>
    </div>
    <div className="p-6">
      <h3 className="font-heading text-xl font-bold text-white mb-2">{program.title}</h3>
      <p className="text-white/60 font-body text-sm mb-4 line-clamp-2">{program.description}</p>
      
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 text-white/50 text-sm">
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {program.duration}
          </span>
          <span className="flex items-center gap-1">
            <Star size={14} className="text-yellow-400" />
            {program.rating}
          </span>
          <span className="flex items-center gap-1">
            <Users size={14} />
            {program.students.toLocaleString()}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-2xl font-heading font-bold text-primary">{program.price}</span>
        <button className="btn-primary text-sm py-2 px-6">
          Enroll Now
        </button>
      </div>
    </div>
  </motion.div>
);

// Regular Program Card
const ProgramCard = ({ program }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-card-bg border border-border rounded-xl overflow-hidden group hover:border-primary/50 transition-all"
  >
    <div className="relative h-40 overflow-hidden">
      <img 
        src={program.image} 
        alt={program.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute top-3 left-3 flex gap-2">
        <span className="bg-black/70 text-white text-xs px-2 py-1 rounded">
          {program.level}
        </span>
      </div>
    </div>
    <div className="p-5">
      <span className="text-primary text-xs font-body">{program.category}</span>
      <h3 className="font-heading text-lg font-bold text-white mt-1 mb-2 line-clamp-2">
        {program.title}
      </h3>
      
      <div className="flex items-center gap-3 text-white/50 text-xs mb-4">
        <span className="flex items-center gap-1">
          <Clock size={12} />
          {program.duration}
        </span>
        <span className="flex items-center gap-1">
          <Star size={12} className="text-yellow-400" />
          {program.rating}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-lg font-heading font-bold text-white">{program.price}</span>
        <button className="text-primary text-sm font-body hover:underline flex items-center gap-1">
          View Details <ChevronRight size={14} />
        </button>
      </div>
    </div>
  </motion.div>
);

export default TrainingPage;
